/**
 * The Load-Out Check (piece 28; decision 0151). 🔨 It fires at dispatch, never
 * at arrival, and it records that somebody said yes: it never claims what is in
 * the truck and it never blocks the dispatch. A Personal item is acknowledged
 * per worker and a Shared item once per visit; the scope is copied from the
 * requirement type, never typed.
 */
export type RequirementScope = 'Personal' | 'Shared';

/** 🔒 The Work Order status whose arrival asks the Load-Out Check: leaving the shop, never arriving on site (decision 0151). */
export const LOAD_OUT_FIRES_AT = 'Dispatched' as const;

/** Whether a move to this status asks the check. Arriving — InProgress, a check-in, a gate sign-in — asks nothing. */
export function loadOutFires(toStatus: string): boolean {
  return toStatus === LOAD_OUT_FIRES_AT;
}

export interface LoadOutRequirement {
  siteRequirementTypeId: string;
  name: string;
  scope: RequirementScope;
  isSerialisedInstrument: boolean;
}

export interface LoadOutLine {
  siteRequirementTypeId: string;
  name: string;
  scope: RequirementScope;
  /** The worker a Personal line asks; null on a Shared line. */
  employmentId: string | null;
  /** 🔨 A serialised instrument prompts for the asset taken; anything else prompts for nothing. */
  asksForAsset: boolean;
}

/** One line per Personal requirement per worker, and one per Shared requirement per visit. */
export function loadOutLines(requirements: readonly LoadOutRequirement[], workerEmploymentIds: readonly string[]): LoadOutLine[] {
  const seen = new Set<string>();
  const unique = requirements.filter((r) => (seen.has(r.siteRequirementTypeId) ? false : (seen.add(r.siteRequirementTypeId), true)));
  const workers = [...new Set(workerEmploymentIds)];
  return unique.flatMap((r): LoadOutLine[] =>
    r.scope === 'Shared'
      ? [{ siteRequirementTypeId: r.siteRequirementTypeId, name: r.name, scope: 'Shared', employmentId: null, asksForAsset: r.isSerialisedInstrument }]
      : workers.map((employmentId) => ({ siteRequirementTypeId: r.siteRequirementTypeId, name: r.name, scope: 'Personal', employmentId, asksForAsset: r.isSerialisedInstrument })),
  );
}

/**
 * 🔒 One line of the check as the Work Order page asks it at dispatch (T2367):
 * the worker named on a Personal line, and whether somebody already said yes.
 */
export interface LoadOutCheckLine extends LoadOutLine {
  /** The worker's name on a Personal line; null on a Shared line. */
  workerName: string | null;
  acknowledged: boolean;
}

/** The visit's Load-Out Check: the lines to acknowledge and the visit's day. */
export interface LoadOutCheckAnswer {
  workOrderId: string;
  visitDate: string;
  lines: LoadOutCheckLine[];
}

export interface AcknowledgementInput {
  siteRequirementTypeId: string;
  employmentId: string | null;
  /** The instrument picked, where the type is a serialised instrument. */
  assetNodeId?: string | null;
}

/** Why an acknowledgement cannot be written as asked, or null. The scope is the type's own, whatever the screen sent. */
export function loadOutAcknowledgementProblem(type: Pick<LoadOutRequirement, 'scope' | 'isSerialisedInstrument' | 'name'>, input: AcknowledgementInput): string | null {
  if (type.scope === 'Personal' && !input.employmentId) return `${type.name} is acknowledged per worker; name the worker`;
  if (type.scope === 'Shared' && input.employmentId) return `${type.name} is shared and acknowledged once for the visit, for nobody in particular`;
  if (type.isSerialisedInstrument && !input.assetNodeId) return `${type.name} is a serialized instrument; pick the one going on the truck`;
  if (!type.isSerialisedInstrument && input.assetNodeId) return `${type.name} is not a serialized instrument, so no asset is issued for it`;
  return null;
}

/** Whose custody an instrument goes to: the worker on a Personal line, else the person acknowledging. */
export function custodianFor(input: AcknowledgementInput, acknowledgedByEmploymentId: string): string {
  return input.employmentId ?? acknowledgedByEmploymentId;
}
